import { useEffect, useState } from 'react';
import { getTransactionById } from '../services/paymentService';

const STATUS_DISPLAY = {
  completed: { label: 'Success', className: 'success' },
  pending: { label: 'Pending', className: 'pending' },
  failed: { label: 'Failed', className: 'failed' },
  refunded: { label: 'Refunded', className: 'refunded' },
};

const PLATFORM_LABELS = {
  ios: 'iOS',
  android: 'Android',
};

function formatDateTime(dateStr) {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '—';
  const day = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  return `${day}, ${time}`;
}

function formatAmount(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(amount));
}

function getStatusDisplay(status) {
  const key = String(status || '').toLowerCase();
  return STATUS_DISPLAY[key] || {
    label: status ? status.charAt(0).toUpperCase() + status.slice(1) : 'Unknown',
    className: key || 'pending',
  };
}

function packageLabel(txn) {
  const petals = txn?.package?.petalCount ?? txn?.petalCount;
  if (petals == null) return 'Petal Pack';
  return petals >= 1000 ? 'Premium Pack' : 'Basic Pack';
}

function Field({ label, children, mono = false }) {
  return (
    <div className="txn-detail-field">
      <span className="txn-detail-field-label">{label}</span>
      <span className={`txn-detail-field-value ${mono ? 'is-mono' : ''}`.trim()}>{children}</span>
    </div>
  );
}

export default function TransactionDetailModal({ open, purchaseId, onClose }) {
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !purchaseId) return undefined;

    let cancelled = false;

    async function loadTransaction() {
      setLoading(true);
      setError('');
      setTransaction(null);

      try {
        const response = await getTransactionById(purchaseId);
        if (cancelled) return;
        setTransaction(response.data || null);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load transaction details.');
        setTransaction(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadTransaction();
    return () => {
      cancelled = true;
    };
  }, [open, purchaseId]);

  if (!open) return null;

  const status = getStatusDisplay(transaction?.status);
  const platform = PLATFORM_LABELS[transaction?.platform] || transaction?.platform || '—';
  const userName = transaction?.user?.name || '—';
  const userInitial = userName !== '—' ? userName.charAt(0).toUpperCase() : '?';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="transaction-detail-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="transaction-detail-title"
      >
        <div className="transaction-detail-modal-header">
          <div className="transaction-detail-modal-heading">
            <div className="transaction-detail-title-row">
              <h2 id="transaction-detail-title">Transaction Details</h2>
              {transaction && (
                <span className={`status-badge status-${status.className}`}>{status.label}</span>
              )}
            </div>
            <p className="transaction-detail-subtitle">
              {transaction?.transactionId || (loading ? 'Loading…' : 'Purchase details')}
            </p>
          </div>
          <button type="button" className="add-restaurant-close" onClick={onClose} aria-label="Close">
            &#x2715;
          </button>
        </div>

        <div className="transaction-detail-modal-body">
          {loading ? (
            <div className="transaction-detail-state">Loading transaction details...</div>
          ) : error ? (
            <div className="transaction-detail-state transaction-detail-state-error">{error}</div>
          ) : !transaction ? (
            <div className="transaction-detail-state">No transaction details found.</div>
          ) : (
            <>
              <div className="txn-detail-summary">
                <div className="txn-detail-summary-item">
                  <span className="txn-detail-summary-label">Amount</span>
                  <span className="txn-detail-summary-value">{formatAmount(transaction.price)}</span>
                </div>
                <div className="txn-detail-summary-item">
                  <span className="txn-detail-summary-label">Petals</span>
                  <span className="txn-detail-summary-value">{transaction.petalCount ?? '—'}</span>
                </div>
                <div className="txn-detail-summary-item">
                  <span className="txn-detail-summary-label">Platform</span>
                  <span className="txn-detail-summary-value">{platform}</span>
                </div>
                <div className="txn-detail-summary-item">
                  <span className="txn-detail-summary-label">Date & Time</span>
                  <span className="txn-detail-summary-value txn-detail-summary-value-sm">
                    {formatDateTime(transaction.createdAt)}
                  </span>
                </div>
              </div>

              <section className="txn-detail-card">
                <h3 className="txn-detail-card-title">Transaction</h3>
                <div className="txn-detail-fields">
                  <Field label="Transaction ID" mono>{transaction.transactionId || '—'}</Field>
                  <Field label="Purchase ID" mono>{transaction.id || '—'}</Field>
                  <Field label="Product ID" mono>{transaction.productId || '—'}</Field>
                </div>
              </section>

              <section className="txn-detail-card">
                <h3 className="txn-detail-card-title">User</h3>
                <div className="txn-detail-user-banner">
                  <div className="txn-detail-user-avatar">{userInitial}</div>
                  <div className="txn-detail-user-meta">
                    <span className="txn-detail-user-name">{userName}</span>
                    <span className="txn-detail-user-email">{transaction.user?.email || '—'}</span>
                  </div>
                </div>
                <div className="txn-detail-fields">
                  <Field label="Phone">{transaction.user?.phone || '—'}</Field>
                  <Field label="User ID" mono>{transaction.user?.id || '—'}</Field>
                </div>
              </section>

              <section className="txn-detail-card">
                <h3 className="txn-detail-card-title">Package</h3>
                <div className="txn-detail-package-banner">
                  <div>
                    <span className="txn-detail-package-name">
                      {packageLabel(transaction)}
                      <img
                        alt=""
                        className="menu-icon-img"
                        src={`${process.env.PUBLIC_URL}/images/star.svg`}
                      />
                    </span>
                    <span className="txn-detail-package-sub">
                      {transaction.package?.petalCount ?? transaction.petalCount ?? 0} Petals
                    </span>
                  </div>
                  <span className="txn-detail-package-price">
                    {formatAmount(transaction.package?.price ?? transaction.price)}
                  </span>
                </div>
                <div className="txn-detail-fields">
                  <Field label="Package ID" mono>{transaction.package?.id || '—'}</Field>
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
